import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  ChartLine, Play, ListChecks, Target, Fire, Trophy, Barbell,
  Drop, MoonStars, Footprints, Brain, Plus, Trash, CheckCircle,
  CalendarBlank, PencilSimple, Sparkle, ArrowRight, Flame,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const TABS = [
  { id: "overview", label: "Overview", icon: ChartLine },
  { id: "today",    label: "Today",    icon: Play },
  { id: "habits",   label: "Habits",   icon: ListChecks },
  { id: "goals",    label: "Goals",    icon: Target },
];

const HABIT_TEMPLATES = [
  { name: "Drink 2L water",      unit: "L",       target_per_day: 2,     category: "water",  icon: "Drop" },
  { name: "Sleep 8 hours",       unit: "hours",   target_per_day: 8,     category: "sleep",  icon: "MoonStars" },
  { name: "10,000 steps",        unit: "steps",   target_per_day: 10000, category: "steps",  icon: "Footprints" },
  { name: "Meditate 10 min",     unit: "min",     target_per_day: 10,    category: "mind",   icon: "Brain" },
  { name: "Workout today",       unit: "session", target_per_day: 1,     category: "workout", icon: "Barbell" },
];

const CAT_ICONS = { water: Drop, sleep: MoonStars, steps: Footprints, mind: Brain, workout: Barbell };

function KpiTile({ icon: Icon, label, value, sub, accent = "text-brand-accent", testid }) {
  return (
    <div className="tactical-card p-4" data-testid={testid}>
      <div className="flex items-start justify-between mb-2">
        <Icon size={18} weight="duotone" className={accent} />
        <span className="label-tiny">{label}</span>
      </div>
      <div className="font-heading text-3xl tabular-nums">{value}</div>
      {sub && <div className="label-tiny text-text-secondary mt-1">{sub}</div>}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Overview
// ─────────────────────────────────────────────────────────────
function OverviewTab({ overview, onGoTab }) {
  return (
    <div className="space-y-6" data-testid="solo-overview-tab">
      <div className="tactical-card p-6" data-testid="solo-overview-hero">
        <div className="label-tiny text-brand-accent mb-1">// TODAY&apos;S GAME PLAN</div>
        <div className="font-heading text-2xl">
          {overview?.workouts_7d ? "KEEP THE MOMENTUM" : "READY WHEN YOU ARE"}
        </div>
        <div className="text-xs text-text-secondary mt-1">
          Solo mode — you set the pace, we track the wins.
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <button onClick={() => onGoTab("today")} className="btn-kinetic px-4 py-2 text-xs label-tiny tracking-widest inline-flex items-center gap-1.5" data-testid="hero-cta-today">
            <Play size={12} weight="bold" /> SEE TODAY
          </button>
          <button onClick={() => onGoTab("habits")} className="border border-white/10 hover:border-brand-accent hover:text-brand-accent px-4 py-2 text-xs label-tiny tracking-widest transition-colors inline-flex items-center gap-1.5" data-testid="hero-cta-habits">
            <ListChecks size={12} weight="bold" /> HABITS
          </button>
          <button onClick={() => onGoTab("goals")} className="border border-white/10 hover:border-brand-accent hover:text-brand-accent px-4 py-2 text-xs label-tiny tracking-widest transition-colors inline-flex items-center gap-1.5" data-testid="hero-cta-goals">
            <Target size={12} weight="bold" /> GOALS
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiTile icon={Fire} label="STREAK" value={overview?.streak_days ?? 0} sub="workout days" accent="text-orange-500" testid="kpi-streak" />
        <KpiTile icon={Barbell} label="WORKOUTS 7D" value={overview?.workouts_7d ?? 0} sub="logged" testid="kpi-workouts-7d" />
        <KpiTile icon={Trophy} label="NEW PRS 30D" value={overview?.prs_30d ?? 0} sub={`${overview?.prs_total ?? 0} all-time`} accent="text-yellow-500" testid="kpi-prs-30d" />
        <KpiTile icon={ListChecks} label="HABITS TODAY" value={`${overview?.habits?.hit_today ?? 0}/${overview?.habits?.total ?? 0}`} sub="hit" accent="text-brand-accent" testid="kpi-habits" />
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="tactical-card p-6" data-testid="overview-goals-card">
          <div className="label-tiny text-brand-accent mb-2">// GOALS</div>
          <div className="font-heading text-3xl tabular-nums">{overview?.goals?.active ?? 0}</div>
          <div className="text-xs text-text-secondary">active · {overview?.goals?.completed ?? 0} completed</div>
          <button onClick={() => onGoTab("goals")} className="mt-3 text-xs label-tiny tracking-widest text-brand-accent hover:text-heat transition-colors inline-flex items-center gap-1">
            OPEN GOALS <ArrowRight size={12} weight="bold" />
          </button>
        </div>
        <div className="tactical-card p-6" data-testid="overview-quick-links">
          <div className="label-tiny text-brand-accent mb-3">// QUICK LINKS</div>
          <div className="grid grid-cols-2 gap-2">
            <Link to="/workouts" className="border border-white/10 hover:border-brand-accent hover:text-brand-accent px-3 py-3 text-xs label-tiny tracking-widest inline-flex items-center gap-1.5 transition-colors" data-testid="ql-log-workout">
              <Barbell size={12} weight="bold" /> LOG WORKOUT
            </Link>
            <Link to="/ai-coach" className="border border-white/10 hover:border-brand-accent hover:text-brand-accent px-3 py-3 text-xs label-tiny tracking-widest inline-flex items-center gap-1.5 transition-colors" data-testid="ql-ai-coach">
              <Sparkle size={12} weight="bold" /> AI COACH
            </Link>
            <Link to="/programs" className="border border-white/10 hover:border-brand-accent hover:text-brand-accent px-3 py-3 text-xs label-tiny tracking-widest inline-flex items-center gap-1.5 transition-colors" data-testid="ql-programs">
              <ListChecks size={12} weight="bold" /> PROGRAMS
            </Link>
            <Link to="/recovery" className="border border-white/10 hover:border-brand-accent hover:text-brand-accent px-3 py-3 text-xs label-tiny tracking-widest inline-flex items-center gap-1.5 transition-colors" data-testid="ql-recovery">
              <MoonStars size={12} weight="bold" /> RECOVERY
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Today
// ─────────────────────────────────────────────────────────────
function TodayTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/solo/today");
      setData(data);
    } catch (e) { /* empty */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  if (loading) return <div className="label-tiny text-text-secondary">LOADING...</div>;
  const session = data?.session;
  const latest = data?.latest_workout;
  return (
    <div className="space-y-6" data-testid="solo-today-tab">
      <div className="tactical-card p-6" data-testid="today-session-card">
        <div className="label-tiny text-brand-accent mb-1">// TODAY&apos;S SESSION</div>
        {session ? (
          <>
            <div className="font-heading text-2xl">{session.session_name}</div>
            <div className="text-xs text-text-secondary mt-1">
              {session.program_name} · WEEK {session.week} · DAY {session.day} · {session.exercise_count} exercises
            </div>
            <div className="mt-3 space-y-1 max-h-56 overflow-y-auto no-scrollbar">
              {(session.exercises || []).map((ex, i) => (
                <div key={i} className="flex items-center justify-between text-sm border border-white/5 px-3 py-2">
                  <div className="font-heading text-sm truncate">{ex.name}</div>
                  <div className="label-tiny text-brand-accent tabular-nums whitespace-nowrap ml-3">
                    {ex.sets || "—"}×{ex.reps || "—"}
                    {ex.weight_kg ? ` @ ${ex.weight_kg}kg` : (ex.percentage_1rm ? ` @ ${ex.percentage_1rm}%` : "")}
                  </div>
                </div>
              ))}
            </div>
            <Link
              to={`/session/${session.assignment_id}/w/${session.week}/d/${session.day}`}
              className="mt-4 btn-kinetic px-4 py-2 text-xs label-tiny tracking-widest inline-flex items-center gap-1.5"
              data-testid="today-start-btn"
            >
              <Play size={12} weight="bold" /> START SESSION
            </Link>
          </>
        ) : (
          <div className="text-center py-6" data-testid="today-empty-session">
            <Play size={32} weight="duotone" className="text-brand-accent mx-auto mb-2" />
            <div className="font-heading text-xl">FREE-FORM DAY</div>
            <p className="text-sm text-text-secondary mt-1">
              No program session queued. Log a free workout or let the AI Coach draft one.
            </p>
            <div className="mt-4 flex flex-wrap gap-2 justify-center">
              <Link to="/workouts" className="btn-kinetic px-4 py-2 text-xs label-tiny tracking-widest inline-flex items-center gap-1.5" data-testid="today-log-freeform">
                <Barbell size={12} weight="bold" /> LOG WORKOUT
              </Link>
              <Link to="/ai-coach" className="border border-white/10 hover:border-brand-accent hover:text-brand-accent px-4 py-2 text-xs label-tiny tracking-widest transition-colors inline-flex items-center gap-1.5" data-testid="today-ai-coach">
                <Sparkle size={12} weight="bold" /> AI COACH
              </Link>
            </div>
          </div>
        )}
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="tactical-card p-6" data-testid="today-habits-summary">
          <div className="label-tiny text-brand-accent mb-2">// HABITS TODAY</div>
          <div className="font-heading text-4xl tabular-nums">
            {data?.habits?.hit_today ?? 0}<span className="text-text-secondary text-lg">/{data?.habits?.total ?? 0}</span>
          </div>
          <div className="text-xs text-text-secondary">hit their daily target</div>
        </div>
        <div className="tactical-card p-6" data-testid="today-last-workout">
          <div className="label-tiny text-brand-accent mb-2">// LAST WORKOUT</div>
          {latest ? (
            <>
              <div className="font-heading text-lg">{latest.name}</div>
              <div className="text-xs text-text-secondary">
                {(latest.date || "").slice(0, 10)} · {latest.duration_min || 0} min
              </div>
            </>
          ) : (
            <div className="text-sm text-text-secondary">No workouts logged yet.</div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Habits — streak tracker + heatmap
// ─────────────────────────────────────────────────────────────
function HabitHeatmap({ habitId }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/solo/habits/${habitId}/heatmap?days=30`).then(({ data }) => setData(data)).catch(() => {});
  }, [habitId]);
  if (!data) return null;
  return (
    <div className="mt-3 flex flex-wrap gap-1" data-testid={`heatmap-${habitId}`}>
      {data.grid.map((cell) => {
        const color = cell.hit
          ? "bg-green-500"
          : cell.pct >= 66 ? "bg-green-500/60"
          : cell.pct >= 33 ? "bg-amber-500/50"
          : cell.pct > 0 ? "bg-red-500/40"
          : "bg-white/5";
        return (
          <div
            key={cell.date}
            title={`${cell.date} · ${cell.value}/${data.target} ${data.unit || ""} · ${cell.pct}%`}
            className={`w-4 h-4 ${color} border border-white/5`}
          />
        );
      })}
    </div>
  );
}

function HabitsTab() {
  const [habits, setHabits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ name: "", unit: "reps", target_per_day: "1", category: "" });

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/solo/habits");
      setHabits(data || []);
    } catch (e) { /* empty */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async (payload) => {
    try {
      await api.post("/solo/habits", payload);
      toast.success("Habit added");
      setForm({ name: "", unit: "reps", target_per_day: "1", category: "" });
      setAddOpen(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const submitCustom = () => {
    if (!form.name.trim()) return toast.error("Name required");
    const t = parseFloat(form.target_per_day);
    if (!t || t <= 0) return toast.error("Target must be > 0");
    create({
      name: form.name.trim(),
      unit: form.unit || "reps",
      target_per_day: t,
      category: form.category || null,
    });
  };

  const logHabit = async (h, value) => {
    try {
      await api.post(`/solo/habits/${h.habit_id}/log`, { value });
      toast.success(`+${value} ${h.unit}`);
      await load();
    } catch (e) {
      toast.error("Failed to log");
    }
  };

  const clearToday = async (h) => {
    try {
      await api.delete(`/solo/habits/${h.habit_id}/log/${new Date().toISOString().slice(0, 10)}`);
      toast.success("Cleared today");
      await load();
    } catch (e) {
      toast.error("Failed");
    }
  };

  const deleteHabit = async (h) => {
    if (!window.confirm(`Delete "${h.name}"? All history will be removed.`)) return;
    try {
      await api.delete(`/solo/habits/${h.habit_id}`);
      toast.success("Habit deleted");
      await load();
    } catch (e) {
      toast.error("Failed");
    }
  };

  if (loading) return <div className="label-tiny text-text-secondary">LOADING...</div>;

  return (
    <div className="space-y-6" data-testid="solo-habits-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="label-tiny text-brand-accent mb-1">// {habits.length} ACTIVE HABIT{habits.length === 1 ? "" : "S"}</div>
          <div className="font-heading text-2xl">STACK THE DAYS.</div>
        </div>
        <button onClick={() => setAddOpen(!addOpen)} className="btn-kinetic px-4 py-2 text-xs label-tiny tracking-widest inline-flex items-center gap-1.5" data-testid="btn-add-habit">
          <Plus size={12} weight="bold" /> ADD HABIT
        </button>
      </div>

      {addOpen && (
        <div className="tactical-card p-5" data-testid="habit-add-form">
          <div className="label-tiny text-brand-accent mb-3">// QUICK START — PICK A TEMPLATE</div>
          <div className="flex flex-wrap gap-2 mb-4">
            {HABIT_TEMPLATES.map((tpl) => {
              const Icon = CAT_ICONS[tpl.category] || ListChecks;
              return (
                <button
                  key={tpl.name}
                  onClick={() => create(tpl)}
                  className="inline-flex items-center gap-1.5 border border-white/10 hover:border-brand-accent hover:text-brand-accent px-3 py-2 text-xs transition-colors"
                  data-testid={`habit-template-${tpl.category}`}
                >
                  <Icon size={12} weight="duotone" /> {tpl.name}
                </button>
              );
            })}
          </div>
          <div className="label-tiny text-brand-accent mb-2">// OR BUILD YOUR OWN</div>
          <div className="grid md:grid-cols-4 gap-2">
            <input
              placeholder="Habit name*"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="md:col-span-2 bg-surface-elevated border border-white/10 px-3 py-2 text-sm focus:outline-none focus:border-brand-accent"
              data-testid="habit-input-name"
            />
            <input
              type="number" step="0.1"
              placeholder="Target/day"
              value={form.target_per_day}
              onChange={(e) => setForm({ ...form, target_per_day: e.target.value })}
              className="bg-surface-elevated border border-white/10 px-3 py-2 text-sm focus:outline-none focus:border-brand-accent"
              data-testid="habit-input-target"
            />
            <input
              placeholder="Unit"
              value={form.unit}
              onChange={(e) => setForm({ ...form, unit: e.target.value })}
              className="bg-surface-elevated border border-white/10 px-3 py-2 text-sm focus:outline-none focus:border-brand-accent"
              data-testid="habit-input-unit"
            />
          </div>
          <div className="mt-3 flex justify-end gap-2">
            <button onClick={() => setAddOpen(false)} className="border border-white/10 px-3 py-1.5 text-xs label-tiny tracking-widest hover:border-white/40 transition-colors">
              CANCEL
            </button>
            <button onClick={submitCustom} className="btn-kinetic px-3 py-1.5 text-xs label-tiny tracking-widest" data-testid="habit-submit-custom">
              ADD CUSTOM
            </button>
          </div>
        </div>
      )}

      {habits.length === 0 ? (
        <div className="tactical-card p-10 text-center" data-testid="habits-empty">
          <ListChecks size={40} weight="duotone" className="text-brand-accent mx-auto mb-3" />
          <div className="font-heading text-xl mb-1">NO HABITS YET</div>
          <p className="text-sm text-text-secondary mb-4">
            Pick a template or build your own. The best habit is the one you actually do.
          </p>
          <button onClick={() => setAddOpen(true)} className="btn-kinetic px-4 py-2 text-xs label-tiny tracking-widest inline-flex items-center gap-1.5">
            <Plus size={12} weight="bold" /> ADD YOUR FIRST HABIT
          </button>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-3">
          {habits.map((h) => {
            const Icon = CAT_ICONS[h.category] || ListChecks;
            const pct = Math.min(100, Math.round(((h.today_value || 0) / (h.target_per_day || 1)) * 100));
            return (
              <div key={h.habit_id} className={`tactical-card p-4 border ${h.today_hit ? "border-green-500/40 bg-green-500/[0.04]" : "border-white/10"}`} data-testid={`habit-${h.habit_id}`}>
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <Icon size={16} weight="duotone" className="text-brand-accent shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="font-heading text-sm truncate">{h.name}</div>
                      <div className="label-tiny text-text-secondary">
                        TARGET: {h.target_per_day} {h.unit}/day
                      </div>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className={`font-heading text-2xl tabular-nums leading-none ${h.streak > 0 ? "text-orange-500" : "text-text-secondary"}`}>
                      {h.streak}
                    </div>
                    <div className="label-tiny inline-flex items-center gap-0.5">
                      <Flame size={9} weight="fill" /> STREAK
                    </div>
                  </div>
                </div>
                {/* Progress bar */}
                <div className="w-full h-1.5 bg-white/5 mb-2">
                  <div className={`h-full ${h.today_hit ? "bg-green-500" : "bg-brand-accent"}`} style={{ width: `${pct}%` }} />
                </div>
                <div className="flex items-center justify-between text-xs">
                  <div className="tabular-nums">
                    <span className="text-brand-accent font-heading text-lg">{h.today_value || 0}</span>
                    <span className="text-text-secondary"> / {h.target_per_day} {h.unit}</span>
                  </div>
                  <div className="label-tiny text-text-secondary">{h.days_hit_30}/30 days hit</div>
                </div>
                {/* Quick add buttons */}
                <div className="mt-3 flex flex-wrap gap-1">
                  <button
                    onClick={() => logHabit(h, 1)}
                    className="border border-white/10 hover:border-brand-accent hover:text-brand-accent px-2 py-1 text-[10px] label-tiny tracking-wider transition-colors"
                    data-testid={`habit-plus1-${h.habit_id}`}
                  >
                    +1
                  </button>
                  <button
                    onClick={() => logHabit(h, h.target_per_day)}
                    className="border border-white/10 hover:border-green-500 hover:text-green-500 px-2 py-1 text-[10px] label-tiny tracking-wider transition-colors"
                    data-testid={`habit-hit-target-${h.habit_id}`}
                  >
                    <CheckCircle size={10} weight="bold" className="inline mr-0.5" /> HIT TARGET
                  </button>
                  {(h.today_value || 0) > 0 && (
                    <button
                      onClick={() => clearToday(h)}
                      className="border border-white/10 hover:border-amber-500 hover:text-amber-500 px-2 py-1 text-[10px] label-tiny tracking-wider transition-colors"
                      data-testid={`habit-clear-${h.habit_id}`}
                    >
                      CLEAR TODAY
                    </button>
                  )}
                  <button
                    onClick={() => deleteHabit(h)}
                    className="ml-auto text-text-secondary hover:text-red-500 transition-colors p-1"
                    data-testid={`habit-delete-${h.habit_id}`}
                  >
                    <Trash size={12} />
                  </button>
                </div>
                <HabitHeatmap habitId={h.habit_id} />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Goals — with progress bars + celebrations
// ─────────────────────────────────────────────────────────────
function GoalsTab() {
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ title: "", target_value: "", current_value: "0", unit: "kg", deadline: "" });

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/goals");
      setGoals(data || []);
    } catch (e) { /* empty */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!form.title.trim()) return toast.error("Title required");
    if (parseFloat(form.target_value) <= 0) return toast.error("Target must be > 0");
    try {
      await api.post("/goals", {
        title: form.title.trim(),
        target_value: parseFloat(form.target_value) || 0,
        current_value: parseFloat(form.current_value) || 0,
        unit: form.unit || "",
        deadline: form.deadline || null,
      });
      toast.success("Goal added");
      setForm({ title: "", target_value: "", current_value: "0", unit: "kg", deadline: "" });
      setAddOpen(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const updateProgress = async (g, delta) => {
    const newVal = (parseFloat(g.current_value) || 0) + delta;
    if (newVal < 0) return;
    try {
      const willComplete = newVal >= g.target_value && g.status === "active";
      await api.patch(`/goals/${g.goal_id}`, {
        current_value: newVal,
        ...(willComplete ? { status: "completed" } : {}),
      });
      if (willComplete) {
        toast.success(`🎯 Goal complete: ${g.title}!`);
      }
      await load();
    } catch (e) {
      toast.error("Failed");
    }
  };

  const setToTarget = async (g) => {
    try {
      await api.patch(`/goals/${g.goal_id}`, {
        current_value: g.target_value,
        status: "completed",
      });
      toast.success(`🎯 Goal complete: ${g.title}!`);
      await load();
    } catch (e) {
      toast.error("Failed");
    }
  };

  const reopen = async (g) => {
    try {
      await api.patch(`/goals/${g.goal_id}`, { status: "active" });
      await load();
    } catch (e) { toast.error("Failed"); }
  };

  const del = async (g) => {
    if (!window.confirm(`Delete "${g.title}"?`)) return;
    try {
      await api.delete(`/goals/${g.goal_id}`);
      toast.success("Deleted");
      await load();
    } catch (e) { toast.error("Failed"); }
  };

  if (loading) return <div className="label-tiny text-text-secondary">LOADING...</div>;
  const active = goals.filter((g) => g.status === "active");
  const done = goals.filter((g) => g.status === "completed");

  return (
    <div className="space-y-6" data-testid="solo-goals-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="label-tiny text-brand-accent mb-1">// {active.length} ACTIVE · {done.length} DONE</div>
          <div className="font-heading text-2xl">CHASE WHAT MATTERS.</div>
        </div>
        <button onClick={() => setAddOpen(!addOpen)} className="btn-kinetic px-4 py-2 text-xs label-tiny tracking-widest inline-flex items-center gap-1.5" data-testid="btn-add-goal">
          <Plus size={12} weight="bold" /> ADD GOAL
        </button>
      </div>

      {addOpen && (
        <div className="tactical-card p-5" data-testid="goal-add-form">
          <div className="grid md:grid-cols-6 gap-2">
            <input
              placeholder="Goal title*"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="md:col-span-3 bg-surface-elevated border border-white/10 px-3 py-2 text-sm focus:outline-none focus:border-brand-accent"
              data-testid="goal-input-title"
            />
            <input
              type="number" step="0.01"
              placeholder="Target*"
              value={form.target_value}
              onChange={(e) => setForm({ ...form, target_value: e.target.value })}
              className="bg-surface-elevated border border-white/10 px-3 py-2 text-sm focus:outline-none focus:border-brand-accent"
              data-testid="goal-input-target"
            />
            <input
              type="number" step="0.01"
              placeholder="Current"
              value={form.current_value}
              onChange={(e) => setForm({ ...form, current_value: e.target.value })}
              className="bg-surface-elevated border border-white/10 px-3 py-2 text-sm focus:outline-none focus:border-brand-accent"
              data-testid="goal-input-current"
            />
            <input
              placeholder="Unit"
              value={form.unit}
              onChange={(e) => setForm({ ...form, unit: e.target.value })}
              className="bg-surface-elevated border border-white/10 px-3 py-2 text-sm focus:outline-none focus:border-brand-accent"
              data-testid="goal-input-unit"
            />
            <input
              type="date"
              value={form.deadline}
              onChange={(e) => setForm({ ...form, deadline: e.target.value })}
              className="md:col-span-2 bg-surface-elevated border border-white/10 px-3 py-2 text-sm focus:outline-none focus:border-brand-accent"
              data-testid="goal-input-deadline"
            />
          </div>
          <div className="mt-3 flex justify-end gap-2">
            <button onClick={() => setAddOpen(false)} className="border border-white/10 px-3 py-1.5 text-xs label-tiny tracking-widest hover:border-white/40 transition-colors">CANCEL</button>
            <button onClick={submit} className="btn-kinetic px-3 py-1.5 text-xs label-tiny tracking-widest" data-testid="goal-submit">
              SAVE GOAL
            </button>
          </div>
        </div>
      )}

      {goals.length === 0 ? (
        <div className="tactical-card p-10 text-center" data-testid="goals-empty">
          <Target size={40} weight="duotone" className="text-brand-accent mx-auto mb-3" />
          <div className="font-heading text-xl mb-1">NO GOALS YET</div>
          <p className="text-sm text-text-secondary mb-4">
            One clear goal beats ten fuzzy ones. Set your first target.
          </p>
          <button onClick={() => setAddOpen(true)} className="btn-kinetic px-4 py-2 text-xs label-tiny tracking-widest inline-flex items-center gap-1.5">
            <Plus size={12} weight="bold" /> SET YOUR FIRST GOAL
          </button>
        </div>
      ) : (
        <>
          {active.length > 0 && (
            <div className="grid md:grid-cols-2 gap-3">
              {active.map((g) => {
                const pct = Math.min(100, Math.round(((g.current_value || 0) / (g.target_value || 1)) * 100));
                const daysLeft = g.deadline ? Math.ceil((new Date(g.deadline) - new Date()) / (1000 * 60 * 60 * 24)) : null;
                return (
                  <div key={g.goal_id} className="tactical-card p-4" data-testid={`goal-${g.goal_id}`}>
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="font-heading text-sm flex-1 min-w-0 truncate">{g.title}</div>
                      <button onClick={() => del(g)} className="text-text-secondary hover:text-red-500 shrink-0" data-testid={`goal-delete-${g.goal_id}`}>
                        <Trash size={12} />
                      </button>
                    </div>
                    <div className="flex items-baseline gap-1 mb-2">
                      <span className="font-heading text-2xl tabular-nums text-brand-accent">{g.current_value || 0}</span>
                      <span className="text-text-secondary text-sm tabular-nums">/ {g.target_value} {g.unit}</span>
                      <span className="ml-auto label-tiny text-text-secondary tabular-nums">{pct}%</span>
                    </div>
                    <div className="w-full h-2 bg-white/5 mb-2">
                      <div className="h-full bg-brand-accent" style={{ width: `${pct}%` }} />
                    </div>
                    <div className="flex items-center justify-between label-tiny text-text-secondary mb-2">
                      {g.deadline ? (
                        <span className="inline-flex items-center gap-1">
                          <CalendarBlank size={10} /> {g.deadline}
                          {daysLeft !== null && (
                            <span className={daysLeft < 0 ? "text-red-500" : daysLeft <= 14 ? "text-amber-500" : ""}>
                              · {daysLeft >= 0 ? `${daysLeft}d LEFT` : `${Math.abs(daysLeft)}d OVERDUE`}
                            </span>
                          )}
                        </span>
                      ) : <span>NO DEADLINE</span>}
                    </div>
                    <div className="flex flex-wrap gap-1">
                      <button onClick={() => updateProgress(g, 1)} className="border border-white/10 hover:border-brand-accent hover:text-brand-accent px-2 py-1 text-[10px] label-tiny tracking-wider transition-colors" data-testid={`goal-plus1-${g.goal_id}`}>+1</button>
                      <button onClick={() => updateProgress(g, 5)} className="border border-white/10 hover:border-brand-accent hover:text-brand-accent px-2 py-1 text-[10px] label-tiny tracking-wider transition-colors" data-testid={`goal-plus5-${g.goal_id}`}>+5</button>
                      <button onClick={() => updateProgress(g, -1)} className="border border-white/10 hover:border-amber-500 hover:text-amber-500 px-2 py-1 text-[10px] label-tiny tracking-wider transition-colors" data-testid={`goal-minus1-${g.goal_id}`}>-1</button>
                      <button onClick={() => setToTarget(g)} className="ml-auto border border-green-500/50 text-green-500 hover:bg-green-500 hover:text-surface-base px-2 py-1 text-[10px] label-tiny tracking-wider transition-colors" data-testid={`goal-complete-${g.goal_id}`}>
                        <CheckCircle size={10} weight="bold" className="inline mr-0.5" /> HIT!
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {done.length > 0 && (
            <div>
              <div className="label-tiny text-brand-accent mb-2 mt-4">// COMPLETED</div>
              <div className="grid md:grid-cols-3 gap-3">
                {done.map((g) => (
                  <div key={g.goal_id} className="border border-green-500/30 bg-green-500/[0.04] p-3" data-testid={`goal-done-${g.goal_id}`}>
                    <div className="flex items-center gap-2">
                      <Trophy size={14} weight="duotone" className="text-green-500 shrink-0" />
                      <div className="min-w-0 flex-1">
                        <div className="font-heading text-sm truncate">{g.title}</div>
                        <div className="label-tiny text-text-secondary">{g.target_value} {g.unit}</div>
                      </div>
                      <button onClick={() => reopen(g)} className="text-text-secondary hover:text-brand-accent text-[10px] label-tiny" data-testid={`goal-reopen-${g.goal_id}`}>REOPEN</button>
                      <button onClick={() => del(g)} className="text-text-secondary hover:text-red-500"><Trash size={11} /></button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Root
// ─────────────────────────────────────────────────────────────
export default function SoloHub() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab") || "overview";
  const tab = TABS.some((t) => t.id === tabParam) ? tabParam : "overview";
  const setTab = (t) => setSearchParams({ tab: t }, { replace: false });
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/solo/hub/overview");
      setOverview(data);
    } catch (e) { /* empty */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto" data-testid="solo-hub-page">
      <div className="mb-6">
        <div className="label-tiny text-brand-accent mb-2 flex items-center gap-2">
          <span className="energy-dot"></span> // SOLO COMMAND DECK
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl">
          <span className="text-heat">{user?.name?.split(" ")[0]?.toUpperCase()}, </span>OWN THE DAY.
        </h1>
        <p className="text-text-secondary text-sm mt-2">
          Habits · Goals · Workouts — self-programmed and self-tracked.
        </p>
      </div>

      <div className="border-b border-white/10 flex gap-1 overflow-x-auto mb-6" data-testid="solo-tabs">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`solo-tab-${t.id}`}
            className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm label-tiny tracking-wider transition border-b-2 -mb-px ${
              tab === t.id ? "border-heat text-heat" : "border-transparent text-text-secondary hover:text-heat"
            }`}
          >
            <t.icon size={14} weight="duotone" /> {t.label.toUpperCase()}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="label-tiny text-text-secondary">LOADING...</div>
      ) : (
        <>
          {tab === "overview" && <OverviewTab overview={overview} onGoTab={setTab} />}
          {tab === "today"    && <TodayTab />}
          {tab === "habits"   && <HabitsTab />}
          {tab === "goals"    && <GoalsTab />}
        </>
      )}
    </div>
  );
}
